import { forwardRef, useEffect, useState } from "react";
import { Fragment, jsx } from "react/jsx-runtime";
//#region src/utils/cn.ts
function cn(...values) {
	return values.filter(Boolean).join(" ");
}
//#endregion
//#region src/primitives/Badge.tsx
var variants$2 = {
	default: "border-primary/30 bg-primary/15 text-primary-foreground",
	secondary: "border-border bg-secondary text-secondary-foreground",
	success: "border-emerald-400/30 bg-emerald-400/15 text-emerald-100",
	destructive: "border-destructive/30 bg-destructive/15 text-destructive-foreground"
};
var Badge = forwardRef(({ className, variant = "default", ...props }, ref) => /* @__PURE__ */ jsx("span", {
	ref,
	className: cn("inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium", variants$2[variant], className),
	...props
}));
Badge.displayName = "Badge";
//#endregion
//#region src/primitives/Button.tsx
var variants$1 = {
	default: "bg-primary text-primary-foreground hover:brightness-110",
	secondary: "border border-border bg-secondary text-secondary-foreground hover:bg-muted",
	destructive: "border border-destructive/60 bg-destructive/20 text-destructive-foreground hover:bg-destructive/30",
	ghost: "bg-transparent text-muted-foreground hover:bg-muted hover:text-foreground"
};
var sizes = {
	sm: "h-8 px-3 text-xs",
	md: "h-10 px-4 text-sm",
	lg: "h-11 px-5 text-base"
};
var Button = forwardRef(({ className, variant = "default", size = "md", ...props }, ref) => /* @__PURE__ */ jsx("button", {
	ref,
	className: cn("inline-flex items-center justify-center gap-2 rounded-md font-bold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50", variants$1[variant], sizes[size], className),
	...props
}));
Button.displayName = "Button";
//#endregion
//#region src/primitives/Card.tsx
var padding = {
	compact: "p-4",
	regular: "p-5",
	spacious: "p-6"
};
var Card = forwardRef(({ className, density = "regular", ...props }, ref) => /* @__PURE__ */ jsx("section", {
	ref,
	className: cn("rounded-[var(--pl-radius-sm)] border border-[var(--pl-line)] bg-[var(--pl-elevated)] text-[var(--pl-text)] shadow-pluto", padding[density], className),
	...props
}));
Card.displayName = "Card";
var CardHeader = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx("div", {
	ref,
	className: cn("space-y-1.5", className),
	...props
}));
CardHeader.displayName = "CardHeader";
var CardTitle = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx("h3", {
	ref,
	className: cn("m-0 text-base font-semibold tracking-tight", className),
	...props
}));
CardTitle.displayName = "CardTitle";
var CardDescription = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx("p", {
	ref,
	className: cn("m-0 text-sm text-muted-foreground", className),
	...props
}));
CardDescription.displayName = "CardDescription";
var CardContent = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx("div", {
	ref,
	className: cn("pt-2", className),
	...props
}));
CardContent.displayName = "CardContent";
//#endregion
//#region src/primitives/Input.tsx
var Input = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx("input", {
	ref,
	className: cn("h-11 w-full rounded-md border border-input bg-background/70 px-3 text-sm text-foreground outline-none transition placeholder:text-muted-foreground focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50", className),
	...props
}));
Input.displayName = "Input";
//#endregion
//#region src/primitives/Label.tsx
var Label = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx("label", {
	ref,
	className: cn("block text-xs font-bold uppercase tracking-[0.08em] text-muted-foreground", className),
	...props
}));
Label.displayName = "Label";
//#endregion
//#region src/primitives/Text.tsx
var variants = {
	body: "text-sm leading-6",
	title: "text-base font-semibold tracking-[-.015em]",
	page: "text-[26px] font-semibold leading-tight tracking-[-.025em]",
	label: "text-[11px] font-bold uppercase tracking-[.14em]"
};
var Text = forwardRef(({ as = "p", variant = "body", className, ...props }, ref) => {
	return /* @__PURE__ */ jsx(as, {
		ref,
		className: cn("m-0", variants[variant], className),
		...props
	});
});
Text.displayName = "Text";
//#endregion
//#region src/primitives/Separator.tsx
function Separator({ orientation = "horizontal", className, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		role: "separator",
		"aria-orientation": orientation,
		className: cn(orientation === "horizontal" ? "h-px w-full bg-[var(--pl-line)]" : "h-full w-px bg-[var(--pl-line)]", className),
		...props
	});
}
//#endregion
//#region src/primitives/Skeleton.tsx
/** Theme-aware shadcn-style loading placeholder. Size it with className. */
var Skeleton = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx("div", {
	ref,
	"data-slot": "skeleton",
	"aria-hidden": "true",
	className: cn("animate-pulse rounded-[var(--pl-radius-xs)] bg-[color-mix(in_srgb,var(--pl-primary)_10%,var(--pl-surface-2))]", className),
	...props
}));
Skeleton.displayName = "Skeleton";
//#endregion
//#region src/primitives/ScrollArea.tsx
/** Native scroll container with the active Pluto theme's scrollbar treatment. */
var ScrollArea = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx("div", {
	ref,
	"data-slot": "scroll-area",
	className: cn("pl-scrollbar overflow-auto", className),
	...props
}));
ScrollArea.displayName = "ScrollArea";
//#endregion
//#region ../gds/tokens/index.ts
var plutoThemes = [
	"Mono",
	"Aurora",
	"Midnight",
	"Paper"
];
var plutoModes = ["dark", "light"];
//#endregion
//#region ../gds/src/index.ts
function applyPlutoTheme(theme, mode) {
	if (typeof document === "undefined") return;
	document.documentElement.dataset.theme = theme;
	document.documentElement.dataset.mode = mode;
}
//#endregion
//#region src/theme/index.tsx
var COOKIE = "ui-preferences";
function cookieDomain() {
	const host = location.hostname;
	if (host === "localhost" || /^[0-9.]+$/.test(host)) return void 0;
	const parts = host.split(".");
	if (parts.length <= 1) return void 0;
	return "." + parts.slice(-2).join(".");
}
function readPrefs() {
	if (typeof document === "undefined") return {};
	const match = document.cookie.split("; ").find((c) => c.startsWith("ui-preferences="));
	if (!match) return {};
	try {
		return JSON.parse(decodeURIComponent(match.slice(15)));
	} catch {
		return {};
	}
}
function writePrefs(patch) {
	if (typeof document === "undefined") return;
	const merged = {
		...readPrefs(),
		...patch
	};
	const secure = location.protocol === "https:";
	const domain = cookieDomain();
	let cookie = `${COOKIE}=${encodeURIComponent(JSON.stringify(merged))}; path=/; max-age=${3600 * 24 * 365}; SameSite=${secure ? "None" : "Lax"}`;
	if (secure) cookie += "; Secure";
	if (domain) cookie += `; Domain=${domain}`;
	document.cookie = cookie;
}
function systemMode() {
	return typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}
function resolveMode(theme) {
	if (theme === "light" || theme === "dark") return theme;
	if (theme === "system") return systemMode();
	return "dark";
}
function resolveVariant(variant) {
	return typeof variant === "string" && plutoThemes.includes(variant) ? variant : "Mono";
}
var themeKey = "pluto.theme";
var modeKey = "pluto.mode";
function usePlutoTheme() {
	const [theme, setThemeState] = useState(() => {
		const prefs = readPrefs();
		if (prefs.themeVariant !== void 0) return resolveVariant(prefs.themeVariant);
		return resolveVariant(typeof localStorage !== "undefined" ? localStorage.getItem(themeKey) : null);
	});
	const [mode, setModeState] = useState(() => {
		const prefs = readPrefs();
		if (prefs.theme !== void 0) return resolveMode(prefs.theme);
		const legacy = typeof localStorage !== "undefined" ? localStorage.getItem(modeKey) : null;
		return legacy === "light" || legacy === "dark" ? legacy : "dark";
	});
	useEffect(() => {
		applyPlutoTheme(theme, mode);
	}, [theme, mode]);
	useEffect(() => {
		const sync = () => {
			const prefs = readPrefs();
			setModeState(resolveMode(prefs.theme));
			if (prefs.themeVariant !== void 0) setThemeState(resolveVariant(prefs.themeVariant));
		};
		window.addEventListener("focus", sync);
		const mq = window.matchMedia("(prefers-color-scheme: dark)");
		const onSystem = () => {
			if ((readPrefs().theme ?? "") === "system") setModeState(systemMode());
		};
		mq.addEventListener("change", onSystem);
		return () => {
			window.removeEventListener("focus", sync);
			mq.removeEventListener("change", onSystem);
		};
	}, []);
	const setTheme = (next) => {
		setThemeState(next);
		writePrefs({ themeVariant: next });
	};
	const setMode = (next) => {
		setModeState(next);
		writePrefs({ theme: next });
	};
	return {
		theme,
		mode,
		themes: plutoThemes,
		modes: plutoModes,
		setTheme,
		setMode
	};
}
function ThemeProvider({ children }) {
	usePlutoTheme();
	return /* @__PURE__ */ jsx(Fragment, { children });
}
//#endregion
export { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Label, ScrollArea, Separator, Skeleton, Text, ThemeProvider, cn, usePlutoTheme };

//# sourceMappingURL=index.js.map